/**
 * Subdomain → tenant resolution.
 *
 * A dedicated subdomain (e.g. `tcn.noirock.app`) pins a tenant's branding/theme
 * BEFORE authentication, when no user profile is available yet (and RLS forbids
 * reading the `tenants` table anonymously). We therefore derive the tenant slug
 * from the hostname alone — purely a front-end branding hint. The real data
 * tenant still comes from `userProfile.tenant_id` post-login (+ RLS).
 *
 * Add a slug here when you give a tenant its own subdomain.
 */
export const SUBDOMAIN_TENANT_SLUGS = ['tcn'];

/**
 * Per-subdomain front-end config. `languages` mirrors the tenant's
 * `enabled_languages` (DB) so the pre-auth login can offer the right set and
 * default — we can't read `tenants` anonymously (RLS). Keep in sync with DB.
 * TCN: enabled_languages = ["ar","en"] (French disabled).
 */
export const SUBDOMAIN_TENANTS = {
  tcn: { slug: 'tcn', name: 'TCN', languages: ['en', 'ar'], defaultLanguage: 'en' },
};

/** Returns the subdomain tenant config for the current host, or null. */
export function getHostTenant() {
  const slug = getHostTenantSlug();
  return slug ? (SUBDOMAIN_TENANTS[slug] ?? null) : null;
}

/**
 * Build-time tenant, injected via `VITE_TENANT` (see .env.tcn). Used as a
 * fallback when the hostname carries no tenant — notably in a bundled Capacitor
 * native build, where the WebView host is always `localhost` and subdomain
 * detection cannot work. Empty on the default (NoiRock/generic) build.
 */
const BUILD_TENANT = (import.meta.env?.VITE_TENANT || '').toLowerCase();

/**
 * Returns the tenant slug encoded in the current hostname's first label, or —
 * when the host carries none (localhost / native build) — the build-time
 * `VITE_TENANT`. Ignores apex (`noirock.app`), `www`, IPs and Vercel preview
 * hosts (`*.vercel.app`).
 */
export function getHostTenantSlug() {
  if (typeof window === 'undefined') return null;
  const host = (window.location.hostname || '').toLowerCase();
  const fromHost = (() => {
    if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
    if (host.endsWith('.vercel.app')) return null;
    const firstLabel = host.split('.')[0];
    return SUBDOMAIN_TENANT_SLUGS.includes(firstLabel) ? firstLabel : null;
  })();
  if (fromHost) return fromHost;
  // Native/bundled build (host = localhost): fall back to the build-time tenant.
  return SUBDOMAIN_TENANT_SLUGS.includes(BUILD_TENANT) ? BUILD_TENANT : null;
}
